import { Link } from 'react-router-dom';

export default function AcompanhamentosPage() {
  const services = [
    {
      id: 'orientacao',
      number: '01',
      title: 'Orientação',
      role: 'Coach & Orientador',
      type: 'Compromisso flexível',
      frequency: '1–2 sessões por mês',
      summary: 'Acompanhamento focado na sessão. A força central é a conversa: perguntas, desafio, perspetiva externa, clarificação. Cada sessão tem objetivo definido e fecha com um resumo escrito dos pontos críticos.\n\nA opção mais leve dos três. Sem materiais construídos para o cliente, sem trabalho contínuo entre encontros.',
      forWhom: [
        'Quem procura orientação regular com baixa intensidade',
        'Quem prefere processar as sessões por si próprio entre encontros',
        'Quem tem uma decisão concreta pela frente e quer pensá-la com alguém de fora',
      ],
      limits: [
        'Comunicação entre sessões esporádica, com resposta até 48h',
        'A implementação é inteiramente da responsabilidade de quem procura este acompanhamento',
      ],
    },
    {
      id: 'estrutura',
      number: '02',
      title: 'Estrutura',
      role: 'Analista & Facilitador Cognitivo',
      type: 'Mínimo 4 meses',
      frequency: '2 sessões por mês',
      summary: 'Tudo o que existe no nível anterior, com a adição da capacidade de análise e escrita estruturada.\n\nAnálise pós-sessão aprofundada, com registo escrito de cada passo, padrões identificados, evolução do trabalho e pontos a aprofundar nos encontros seguintes. Fichas de acompanhamento construídas para o cliente. Revisão a cada dois meses para avaliar percurso e reajustar prioridades.\n\nO foco está na continuidade entre sessões. O que se fala fica registado, organizado e devolvido em forma escrita.',
      forWhom: [
        'Quem quer mais do que conversa: registo, análise, materiais construídos para si',
        'Quem reconhece que mudança séria precisa de tempo e continuidade',
        'Quem valoriza ter um registo do pensamento e das decisões ao longo do trabalho',
      ],
      limits: [
        'Não substitui terapia nem outras formas de apoio especializado',
        'Não inclui gestão de tarefas, assistência executiva nem execução operacional',
        'Comunicação entre sessões com regras definidas, com resposta até 24h',
      ],
    },
    {
      id: 'arquitetura',
      number: '03',
      title: 'Arquitetura',
      role: 'Arquiteto Cognitivo',
      type: 'Mínimo 12 meses',
      frequency: '3–4 sessões por mês',
      summary: 'O nível mais completo, pensado para um número reduzido de pessoas. Inclui tudo o que existe nos níveis anteriores, com a adição de uma terceira camada de trabalho: a construção de estruturas próprias.\n\nMapas mentais personalizados para representar áreas críticas, relações e prioridades. Frameworks e ferramentas individualizadas, desenhadas a partir do contexto e da forma de pensar de cada pessoa. Sistemas de decisão próprios, construídos ativamente e revistos em ciclos mensais e trimestrais.\n\nO que distingue este nível não é a intensidade do problema. É o nível de envolvimento, o detalhe do trabalho e a profundidade do acompanhamento. Trabalha-se sobre o sistema, não sobre os problemas. Em alguns momentos, o meu papel aproxima-se mais do de um thinking partner do que do de um coach.',
      forWhom: [
        'Quem quer o máximo de envolvimento, estrutura e recursos disponíveis',
        'Quem decide muito, decide rápido, e quer fazê-lo com mais clareza e menos ruído',
        'Quem procura um acompanhamento que se sustente no tempo e cresça em profundidade',
      ],
      limits: [
        'Não é terapia, gestão de tarefas, assistência executiva nem execução operacional',
        'Não é disponibilidade ilimitada',
        'Comunicação entre sessões com regras definidas, com resposta até 4h',
        'A responsabilidade pelas decisões e pela implementação é sempre de quem está do outro lado',
      ],
      hasDetailedInfo: true,
    },
  ];

  return (
    <main className="pt-24" data-testid="acompanhamentos-page">
      {/* Hero */}
      <section className="section">
        <div className="page-container">
          <div className="max-w-3xl">
            <span className="label-mono mb-4 block">Serviços</span>
            <h1 className="heading-display mb-8">
              Acompanhamentos
            </h1>
            <p className="body-text text-lg">
              Três níveis de acompanhamento. Cada nível inclui o anterior e acrescenta
              uma camada de trabalho: a conversa, depois a análise e a escrita,
              depois a construção de sistemas próprios.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section section-muted" data-testid="services-grid">
        <div className="page-container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#E6E4DC]">
            {services.map((service) => (
              <article 
                key={service.id}
                id={service.id}
                className="bg-[#FDFCF8] p-8 lg:p-10"
                data-testid={`service-${service.id}`}
              >
                <span className="label-mono mb-6 block">{service.number}</span>
                
                <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-2">
                  {service.title}
                </h2>
                
                <p className="font-mono text-xs uppercase tracking-widest text-[#5A5A5A] mb-6">
                  {service.role}
                </p>

                <div className="flex items-center gap-4 mb-6">
                  <span className="text-sm text-[#5A5A5A] border border-[#E6E4DC] px-3 py-1">
                    {service.type}
                  </span>
                  <span className="text-sm text-[#5A5A5A]">
                    {service.frequency}
                  </span>
                </div>

                <p className="body-text mb-8 whitespace-pre-line">
                  {service.summary}
                </p>

                <div className="mb-8">
                  <h3 className="font-mono text-xs uppercase tracking-widest text-[#1A1A1A] mb-4">
                    Para quem faz sentido
                  </h3>
                  <ul className="space-y-2">
                    {service.forWhom.map((item, index) => (
                      <li key={index} className="text-sm text-[#5A5A5A] pl-4 border-l border-[#E6E4DC]">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="font-mono text-xs uppercase tracking-widest text-[#1A1A1A] mb-4">
                    Limites claros
                  </h3>
                  <ul className="space-y-2">
                    {service.limits.map((item, index) => (
                      <li key={index} className="text-sm text-[#5A5A5A] pl-4 border-l border-[#E6E4DC]">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="section" data-testid="note-section">
        <div className="page-container">
          <div className="max-w-2xl">
            <span className="label-mono mb-4 block">Nota</span>
            <h2 className="heading-section mb-6">
              O nível certo é o que serve agora.
            </h2>
            <p className="body-text mb-6">
              Não existe progressão obrigatória. Os níveis distinguem-se pelo tipo e pela profundidade do trabalho, não pela seriedade do "problema".
            </p>
            <p className="body-text">
              A escolha do nível adequado faz-se em conversa, 
              tendo em conta o momento de vida, a necessidade específica 
              e o nível de autonomia de cada pessoa.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section-muted">
        <div className="page-container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-section mb-6">
              Que nível faz sentido para si?
            </h2>
            <p className="body-text mb-8">
              A decisão não se toma num website. 
              O primeiro passo é sempre uma conversa.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/processo" className="btn-outline" data-testid="cta-process">
                Ver Processo
              </Link>
              <Link to="/contacto" className="btn-primary" data-testid="cta-contact">
                Iniciar Conversa
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
